import { useState, useMemo, useEffect } from 'react';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { StoryCard } from '../components/stories/StoryCard';
import { CountryFilter } from '../components/CountryFilter';
import { useStories, useStorySearch, type SearchDistribution } from '../hooks/stories';
import { vendorRoutingUiEnabled } from '../components/vendor-content/routing-ui-flag';
import { useFilterDefaultsFromPrefs } from '../hooks/useFilterDefaultsFromPrefs';
import { trackStoriesListViewed } from '../lib/analytics-events';
import {
  SOURCE_TIERS,
  STORY_TIME_WINDOWS,
  type ConfidenceLabel,
  type Severity,
  type SourceTier,
  type StoryFilters,
  type StoryTimeWindow,
} from '../types/story';

const SECTIONS = [
  'vulnerabilities_exploitation',
  'vendor_intelligence',
  'supply_chain_third_party',
  'threat_landscape_ir',
  'cloud_saas_identity',
  'industry_news',
];

const CONFIDENCES: ConfidenceLabel[] = ['established', 'corroborated', 'reported', 'single_source', 'unverified'];
const SEVERITIES: Severity[] = ['critical', 'high', 'medium', 'low'];

// Curated industry slugs surfaced in the rail. Matches the canonical
// ontology terms attached to articles by the topic-classifier (subset that
// has meaningful coverage in prod). Backed by `articles.industries[]` via
// the API's `industry` Zod param + `arrayOverlaps` clause.
const INDUSTRIES = [
  'healthcare',
  'finance',
  'energy',
  'public_sector',
  'manufacturing',
  'retail',
  'technology',
  'telecom',
];

/**
 * Map a preset time-window to an ISO-8601 lower bound (`from`).
 * `all` returns `undefined` to clear the bound; the API treats absence as
 * "no time scope".
 */
function timeWindowToFromISO(win: StoryTimeWindow): string | undefined {
  if (win === 'all') return undefined;
  const now = Date.now();
  const HOUR = 60 * 60 * 1000;
  const DAY = 24 * HOUR;
  const offsetMs = win === '24h' ? 24 * HOUR : win === '7d' ? 7 * DAY : win === '30d' ? 30 * DAY : 90 * DAY;
  return new Date(now - offsetMs).toISOString();
}

/**
 * Snap a saved-default `from` ISO string back to the closest preset window
 * for display. Anything older than 90d clamps to "all". Used so the time-
 * window dropdown reflects what the saved-default seeded.
 */
function fromISOToTimeWindow(fromISO: string | undefined): StoryTimeWindow {
  if (!fromISO) return 'all';
  const ageMs = Date.now() - new Date(fromISO).getTime();
  const HOUR = 60 * 60 * 1000;
  const DAY = 24 * HOUR;
  if (ageMs <= 24 * HOUR + HOUR) return '24h';
  if (ageMs <= 7 * DAY + DAY) return '7d';
  if (ageMs <= 30 * DAY + DAY) return '30d';
  if (ageMs <= 90 * DAY + DAY) return '90d';
  return 'all';
}

export function StoriesListPage() {
  const [search, setSearch] = useState('');
  // P5 primitive adoption — required by spec rule §10 and ESLint
  // `filter-rail-must-adopt-prefs`. CALL-site detection only; the helper is
  // typed to <StoryFilters> so saved-search payloads land on the page-level
  // filter shape directly.
  const { defaults } = useFilterDefaultsFromPrefs<StoryFilters>('stories');
  const [filters, setFilters] = useState<StoryFilters>(() =>
    defaults ?? { limit: 20 },
  );
  const [timeWindow, setTimeWindow] = useState<StoryTimeWindow>(() =>
    fromISOToTimeWindow(defaults?.from),
  );
  // KAN-822 T9 — search's result-type filter, for this request only (dark).
  const vendorUi = vendorRoutingUiEnabled();
  const [distribution, setDistribution] = useState<SearchDistribution | undefined>(undefined);
  const searchResult = useStorySearch(search.trim(), vendorUi ? distribution : undefined);
  const listResult = useStories(filters);

  // KAN-822 T9 (codex R1) — browsing and searching are separate modes: the
  // Vendor browse view applies only with no search, the result-type filter
  // only during one. Entering a search leaves the browse view (All stories);
  // leaving it clears the filter, so neither mode's state leaks into the other.
  const onSearchChange = (next: string) => {
    const wasSearching = search.trim().length > 0;
    const searching = next.trim().length > 0;
    if (!wasSearching && searching && filters.view) {
      setFilters((f) => {
        const cleared: StoryFilters = { ...f, cursor: undefined };
        delete cleared.view;
        return cleared;
      });
    }
    if (wasSearching && !searching) setDistribution(undefined);
    setSearch(next);
  };
  const searching = search.trim().length > 0;

  // KAN-32 stories:list_viewed — keyed on the filter set, not on render.
  useEffect(() => {
    trackStoriesListViewed(filters);
  }, [filters]);

  const stories = useMemo(() => {
    if (search.trim()) return searchResult.data?.stories ?? [];
    return listResult.data?.stories ?? [];
  }, [search, searchResult.data, listResult.data]);

  const isLoading = search.trim() ? searchResult.isLoading : listResult.isLoading;
  const nextCursor = listResult.data?.nextCursor ?? null;

  return (
    <div className="space-y-6 p-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Stories</h1>
          <p className="text-sm text-muted-foreground">Clusters of related coverage across the sections you follow.</p>
        </div>
        <Input
          type="search"
          placeholder="Search stories…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="max-w-sm"
          aria-label="Search stories"
        />
      </header>

      {/* KAN-822 T9 — the dedicated Vendor view (spec R14): a browsing mode for
          this page only; it reads and writes no saved preference (dark). */}
      {vendorUi && (
        <div className="flex flex-wrap items-center gap-3">
          {!searching && (
            <div
              role="tablist"
              aria-label="Story view"
              className="inline-flex w-fit items-center gap-1 rounded-md border bg-muted p-1 text-xs"
            >
              {(
                [
                  ['all', 'All stories'],
                  ['vendor', 'Vendor updates'],
                ] as const
              ).map(([key, label]) => {
                const selected = key === 'vendor' ? filters.view === 'vendor' : filters.view !== 'vendor';
                return (
                  <button
                    key={key}
                    role="tab"
                    type="button"
                    aria-selected={selected}
                    onClick={() =>
                      setFilters((f) => {
                        const next: StoryFilters = { ...f, cursor: undefined };
                        if (key === 'vendor') next.view = 'vendor';
                        else delete next.view;
                        return next;
                      })
                    }
                    className={
                      'rounded px-3 py-1 transition-colors ' +
                      (selected ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')
                    }
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}
          {!searching && filters.view === 'vendor' && (
            <p className="text-sm text-muted-foreground">Browsing vendor updates. Your feed settings do not change.</p>
          )}
          {searching && (
            <label className="ml-auto inline-flex items-center gap-2 text-xs text-muted-foreground">
              Result type
              <select
                className="h-9 rounded-md border bg-background px-2 text-sm text-foreground"
                value={distribution ?? 'all'}
                onChange={(e) =>
                  setDistribution(e.target.value === 'all' ? undefined : (e.target.value as SearchDistribution))
                }
              >
                <option value="all">All results</option>
                <option value="mainstream">Without vendor updates</option>
                <option value="vendor">Vendor updates only</option>
              </select>
            </label>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Select
          value={filters.section ?? 'all'}
          onValueChange={(v) => setFilters((f) => ({ ...f, section: v === 'all' ? undefined : v, cursor: undefined }))}
        >
          <SelectTrigger className="w-56" aria-label="Filter by section"><SelectValue placeholder="Section" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sections</SelectItem>
            {SECTIONS.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select
          value={filters.confidence ?? 'all'}
          onValueChange={(v) => setFilters((f) => ({ ...f, confidence: v === 'all' ? undefined : (v as ConfidenceLabel), cursor: undefined }))}
        >
          <SelectTrigger className="w-48" aria-label="Filter by confidence"><SelectValue placeholder="Confidence" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All confidence</SelectItem>
            {CONFIDENCES.map((c) => <SelectItem key={c} value={c}>{c.replace('_', ' ')}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select
          value={filters.severity ?? 'all'}
          onValueChange={(v) => setFilters((f) => ({ ...f, severity: v === 'all' ? undefined : (v as Severity), cursor: undefined }))}
        >
          <SelectTrigger className="w-40" aria-label="Filter by severity"><SelectValue placeholder="Severity" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All severity</SelectItem>
            {SEVERITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        {/* P4a (item 5): source-tier filter. Backed by `sources.tier` via
            canonical-article join. */}
        <Select
          value={filters.tier ?? 'all'}
          onValueChange={(v) => setFilters((f) => ({ ...f, tier: v === 'all' ? undefined : (v as SourceTier), cursor: undefined }))}
        >
          <SelectTrigger className="w-44" aria-label="Filter by source tier"><SelectValue placeholder="Source tier" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tiers</SelectItem>
            {SOURCE_TIERS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        {/* P4a (item 5): industry filter. Backed by `articles.industries[]`
            arrayOverlaps. */}
        <Select
          value={filters.industry ?? 'all'}
          onValueChange={(v) => setFilters((f) => ({ ...f, industry: v === 'all' ? undefined : v, cursor: undefined }))}
        >
          <SelectTrigger className="w-44" aria-label="Filter by industry"><SelectValue placeholder="Industry" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All industries</SelectItem>
            {INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i.replace(/_/g, ' ')}</SelectItem>)}
          </SelectContent>
        </Select>
        {/* P4a (item 5): preset time-window. Maps to `from` ISO-8601 on the
            request; `all` clears the lower bound. */}
        <Select
          value={timeWindow}
          onValueChange={(v) => {
            const next = v as StoryTimeWindow;
            setTimeWindow(next);
            setFilters((f) => ({ ...f, from: timeWindowToFromISO(next), cursor: undefined }));
          }}
        >
          <SelectTrigger className="w-40" aria-label="Filter by time window"><SelectValue placeholder="Time window" /></SelectTrigger>
          <SelectContent>
            {STORY_TIME_WINDOWS.map((w) => (
              <SelectItem key={w} value={w}>
                {w === 'all' ? 'All time' : `Last ${w}`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">Country</label>
          <CountryFilter
            selected={filters.country ?? []}
            onChange={(next) =>
              setFilters((f) => ({
                ...f,
                country: next.length > 0 ? next : undefined,
                cursor: undefined,
              }))
            }
          />
        </div>
      </div>

      {isLoading && <p className="py-10 text-center text-sm text-muted-foreground">Loading stories…</p>}

      {!isLoading && stories.length === 0 && (
        <p className="py-10 text-center text-sm text-muted-foreground">No stories match the current filters.</p>
      )}

      <div
        className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"
        data-testid="stories-grid"
      >
        {stories.map((s) => <StoryCard key={s.id} story={s} />)}
      </div>

      {!search.trim() && nextCursor && (
        <div className="flex justify-center">
          <Button variant="secondary" onClick={() => setFilters((f) => ({ ...f, cursor: nextCursor }))}>
            Load more
          </Button>
        </div>
      )}
    </div>
  );
}
